/**
 * Right-panel detail for one UCP checkout session (founder decision
 * 2026-10-23). Shows the append-only event timeline:
 *
 *   CREATED              2 cart lines
 *   UPDATED              Buyer added
 *   READY FOR COMPLETE   Shipping option selected
 *   COMPLETED            Signed record →
 *   EXPIRED              (if abandoned past TTL)
 *
 * The `Signed record` link opens the ADR-0059 record detail for the
 * `audit_record_id` the `completed` event carries. Everything else is
 * UNSIGNED behavioural metadata — same posture as ADR-0203 page/search
 * tracking: never a second source of truth for a signed decision.
 */

import { useEffect, useState } from 'react';

import type { SiteId } from '@sill/types';
import type { UcpSessionDetailResponse, UcpSessionEvent } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { Card, RelativeTime } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { formatMoney, statusLabel } from './UcpSessionsList.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface UcpSessionDetailProps {
  readonly siteId: SiteId;
  readonly sessionId: string;
  readonly onOpenAuditRecord?: (record_id: string) => void;
}

export function UcpSessionDetail({ siteId, sessionId, onOpenAuditRecord }: UcpSessionDetailProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const [data, setData] = useState<UcpSessionDetailResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const get = api.mandates.getUcpSession;
    if (get === undefined) {
      setFailed(true);
      return undefined;
    }
    let cancelled = false;
    setData(null);
    setFailed(false);
    void get
      .call(api.mandates, siteId, sessionId)
      .then((res) => {
        if (cancelled) return;
        if (res === null) {
          setFailed(true);
          return;
        }
        setData(res);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, siteId, sessionId]);

  if (failed) {
    return (
      <Card className="p-4 sm:p-6">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5 }}>
          Session could not be loaded.
        </div>
      </Card>
    );
  }
  if (data === null) {
    return (
      <Card className="p-4 sm:p-6">
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>
          LOADING SESSION…
        </div>
      </Card>
    );
  }

  const s = data.session;
  const total = formatMoney(s.cart_total_minor, s.currency ?? 'USD');
  const label = s.expired && s.status !== 'completed' && s.status !== 'canceled' ? 'Expired' : statusLabel(s.status);

  return (
    <div data-testid="ucp-session-detail">
      <Card className="p-4 sm:p-6">
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          UCP CHECKOUT SESSION
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 18, color: C.text, marginBottom: 4 }}>{label}</div>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 0.5 }}>
          {s.platform_host} · {total} · {s.line_count} line{s.line_count === 1 ? '' : 's'}
          {s.ap2_negotiated ? ' · AP2' : ''} · {s.platform_auth === 'api_key' ? 'API key' : 'Signed'}
        </div>
        {s.audit_record_id !== null && onOpenAuditRecord !== undefined ? (
          <button
            type="button"
            onClick={() => onOpenAuditRecord(s.audit_record_id as string)}
            style={{
              marginTop: 10,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 10px',
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.limeFg,
              cursor: 'pointer',
            }}
          >
            SIGNED RECORD →
          </button>
        ) : null}
        <div style={{ marginTop: 16 }}>
          <div
            style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 8 }}
          >
            TIMELINE
          </div>
          <div style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}>
            {data.events.map((e, i) => (
              <EventRow key={e.event_id ?? `virtual.${i}`} event={e} isLast={i === data.events.length - 1} />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function EventRow({ event, isLast }: { event: UcpSessionEvent; isLast: boolean }): JSX.Element {
  const C = useC();
  const amount = formatMoney(event.cart_total_minor, event.currency ?? 'USD');
  const summary = describeEvent(event);
  return (
    <div
      className="p-3 flex items-start gap-3"
      style={{
        borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
      }}
    >
      <RelativeTime
        iso={event.occurred_at}
        style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, flexShrink: 0, minWidth: 72 }}
      />
      <div className="flex-1 min-w-0">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: toneForEvent(event, C),
            letterSpacing: 1.2,
          }}
        >
          {eventLabel(event.event_type)}
        </div>
        {summary !== null ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, marginTop: 2 }}>{summary}</div>
        ) : null}
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 0.5, marginTop: 2 }}>
          {amount} · {event.line_count} line{event.line_count === 1 ? '' : 's'}
          {event.audit_record_id !== null ? ' · signed' : ''}
        </div>
      </div>
    </div>
  );
}

function eventLabel(t: UcpSessionEvent['event_type']): string {
  switch (t) {
    case 'created':
      return 'CREATED';
    case 'updated':
      return 'UPDATED';
    case 'ready_for_complete':
      return 'READY FOR COMPLETE';
    case 'completed':
      return 'COMPLETED';
    case 'canceled':
      return 'CANCELED';
    case 'expired':
      return 'EXPIRED';
  }
}

function toneForEvent(event: UcpSessionEvent, C: ReturnType<typeof useC>): string {
  switch (event.event_type) {
    case 'completed':
      return C.limeFg;
    case 'canceled':
      return C.coralFg;
    case 'expired':
      return C.amberFg;
    case 'ready_for_complete':
      return C.skyFg;
    default:
      return C.muted;
  }
}

/** Human-readable summary from change_summary booleans. No PII reaches here. */
function describeEvent(event: UcpSessionEvent): string | null {
  if (event.event_type === 'created') {
    const bits: string[] = [`${event.line_count} line${event.line_count === 1 ? '' : 's'}`];
    const summary = event.change_summary;
    if (summary['buyer_present'] === true) bits.push('buyer on file');
    if (summary['fulfillment_selected'] === true) bits.push('shipping picked');
    if (summary['discounts_applied'] === true) bits.push('discount applied');
    return bits.join(' · ');
  }
  if (event.event_type === 'updated' || event.event_type === 'ready_for_complete') {
    const changed: string[] = [];
    const summary = event.change_summary;
    if (summary['line_items_changed'] === true) changed.push('line items');
    if (summary['buyer_changed'] === true) changed.push('buyer');
    if (summary['fulfillment_changed'] === true) changed.push('fulfillment');
    if (summary['discounts_changed'] === true) changed.push('discount');
    if (changed.length === 0) return null;
    return `Changed: ${changed.join(', ')}`;
  }
  return null;
}
